import { HttpClient } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, Observable, of, shareReplay, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  role: string;
  canAddClients: boolean;
  organisationId: string;
  subscriptionStatus: 'unpaid' | 'active' | 'paused' | 'cancelled';
  accountPlan: 'solo' | 'team';
  freeClientUsed: boolean;
  foundingMember: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_ROLE_KEY = 'user_role';
  private readonly USER_CAN_ADD_CLIENTS_KEY = 'user_can_add_clients';
  private readonly USER_NAME_KEY = 'user_name';

  isAuthenticated = signal(this.hasToken());
  userRole = signal<string | null>(this.getStoredRole());
  userCanAddClients = signal<boolean>(this.getStoredCanAddClients());
  userName = signal<string | null>(localStorage.getItem(this.USER_NAME_KEY));

  isAdmin = computed(() => this.userRole() === 'owner');
  isMember = computed(() => this.userRole() === 'member');
  canAddClients = computed(() => this.userCanAddClients());
  roleLabel = computed(() => this.userRole() === 'owner' ? 'Admin' : 'Member');

  constructor(private http: HttpClient, private router: Router) {
    // If already authenticated on init, refresh the profile in background
    if (this.hasToken()) {
      this.fetchProfile();
    }
  }

  login(email: string, password: string) {
    return this.http.post<AuthTokens>(`${environment.apiUrl}/auth/login`, { email, password }).pipe(
      tap((tokens) => {
        this.storeTokens(tokens);
        this.fetchProfile();
      }),
    );
  }

  register(data: any) {
    return this.http.post<AuthTokens>(`${environment.apiUrl}/auth/register`, data).pipe(
      tap((tokens) => {
        this.storeTokens(tokens);
        this.fetchProfile();
      }),
    );
  }

  logout() {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      this.http.post(`${environment.apiUrl}/auth/logout`, { refreshToken }).subscribe();
    }
    this.clearTokens();
    this.isAuthenticated.set(false);
    this.userRole.set(null);
    this.userCanAddClients.set(false);
    this.userName.set(null);
    this.router.navigate(['/auth/login']);
  }

  /**
   * One refresh at a time. Refresh tokens rotate on use, so when several
   * requests hit a 401 together (any page that loads in parallel), each
   * refreshing with the same token meant only the first succeeded and the
   * rest logged the user out. Concurrent callers now share one request.
   */
  private refreshInFlight$: Observable<AuthTokens> | null = null;

  refreshTokens(): Observable<AuthTokens> {
    if (this.refreshInFlight$) return this.refreshInFlight$;

    const refreshToken = this.getRefreshToken();
    this.refreshInFlight$ = this.http
      .post<AuthTokens>(`${environment.apiUrl}/auth/refresh`, { refreshToken })
      .pipe(
        tap((tokens) => {
          this.storeTokens(tokens);
          this.fetchProfile();
        }),
        catchError((err) => {
          // Another tab (same localStorage) may have rotated the token first —
          // then ours was rejected but valid tokens are already stored.
          const current = this.getRefreshToken();
          const access = this.getAccessToken();
          if (current && access && current !== refreshToken) {
            return of({ accessToken: access, refreshToken: current });
          }
          return throwError(() => err);
        }),
        finalize(() => (this.refreshInFlight$ = null)),
        shareReplay(1),
      );
    return this.refreshInFlight$;
  }

  forgotPassword(email: string) {
    return this.http.post(`${environment.apiUrl}/auth/forgot-password`, { email });
  }

  resetPassword(token: string, password: string) {
    return this.http.post(`${environment.apiUrl}/auth/reset-password`, { token, password });
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  fetchProfile() {
    this.http.get<UserProfile>(`${environment.apiUrl}/auth/me`).subscribe({
      next: (profile) => {
        this.userRole.set(profile.role);
        this.userCanAddClients.set(profile.canAddClients);
        this.userName.set(profile.name);
        // Persist so it survives page refresh
        localStorage.setItem(this.USER_ROLE_KEY, profile.role);
        localStorage.setItem(this.USER_CAN_ADD_CLIENTS_KEY, String(profile.canAddClients));
        localStorage.setItem(this.USER_NAME_KEY, profile.name);
      },
      error: () => {
        // Don't reset if we have stored values — only reset on explicit logout
      },
    });
  }

  private storeTokens(tokens: AuthTokens) {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refreshToken);
    this.isAuthenticated.set(true);
  }

  private clearTokens() {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_ROLE_KEY);
    localStorage.removeItem(this.USER_CAN_ADD_CLIENTS_KEY);
    localStorage.removeItem(this.USER_NAME_KEY);
  }

  private hasToken(): boolean {
    return !!localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  private getStoredRole(): string | null {
    return localStorage.getItem(this.USER_ROLE_KEY);
  }

  private getStoredCanAddClients(): boolean {
    return localStorage.getItem(this.USER_CAN_ADD_CLIENTS_KEY) === 'true';
  }
}
